import Phaser from 'phaser';
import { t } from '@i18n/index';
import { PALETTE, titleStyle, readoutStyle, FONT, px } from '../theme';
import { createButton } from '../ui/button';
import { incidentType } from '@core/mission/incident';
import type { MissionResult } from '@core/campaign/campaign';
import type { OutcomeTier } from '@core/mission/outcome';
import { audio } from '../audio/engine';
import { DRONE_ROOT, SITE_MOTIF } from '../audio/motifs';
import { maybeShowTutorial } from '../ui/adviserPanel';
import { plateKey, TIER_PLATE } from '../assets';
import type { Campaign } from '@core/campaign/campaign';
import type { VesselId } from '@core/voyage/profiles';

const TIER_COLOUR: Record<OutcomeTier, string> = {
  rescue: '#8fd9c4',
  salvage: '#e0b070',
  cold: '#7f95a8',
  nothing: '#c9736f',
};

/**
 * Was am Einsatzort vorgefunden wurde.
 *
 * Der Moment, in dem sich die Entscheidungen der Reise auszahlen — oder eben
 * nicht. Die geborgenen Hinweise werden hier zum ersten Mal im Wortlaut
 * gezeigt; sie sind der eigentliche Ertrag der Mission.
 */
export class SiteScene extends Phaser.Scene {
  constructor() {
    super('Site');
  }

  /**
   * Das Stufenbild als grossflaechiger Hintergrund.
   *
   * Stark abgedunkelt und nach oben ausgeblendet: Es traegt die Stimmung,
   * darf aber unter keinen Umstaenden mit dem Text konkurrieren, der darauf
   * steht — die Hinweise sind der eigentliche Ertrag der Mission.
   */
  private renderTierPlate(tier: OutcomeTier, width: number): void {
    const key = plateKey(TIER_PLATE[tier]);
    if (!this.textures.exists(key)) return;

    const image = this.add.image(width / 2, 0, key).setOrigin(0.5, 0).setDepth(-30);
    const scale = width / image.width;
    image.setScale(scale).setAlpha(0.26);
  }

  create(): void {
    const result = this.registry.get('missionResult') as MissionResult & {
      stranded: boolean;
      vesselId: VesselId;
    };
    const campaign = this.registry.get('campaign') as Campaign;
    this.cameras.main.setBackgroundColor(PALETTE.abyss);

    const { width, height } = this.scale;
    const centreX = width / 2;
    const panelWidth = Math.min(640, width - 80);
    const left = centreX - panelWidth / 2;

    // Ein Bild zur erreichten Stufe — nicht zum gesuchten Schiff. Es zeigt,
    // was man vorgefunden hat: Menschen im Floss, geborgenes Geraet an Deck,
    // ein leeres Floss im letzten Licht, oder nichts als Duenung.
    this.renderTierPlate(result.tier, width);

    this.add.text(centreX, 44, t('site.title'), titleStyle(22)).setOrigin(0.5);
    this.add
      .text(
        centreX,
        74,
        `${t(incidentType(result.incident.kind).nameKey)} · ${result.incident.vesselName}`,
        readoutStyle(12, true),
      )
      .setOrigin(0.5);

    this.add
      .text(centreX, 108, t(`outcome.${result.tier}`), {
        fontFamily: FONT.display,
        fontSize: px(26),
        color: TIER_COLOUR[result.tier],
      })
      .setOrigin(0.5);

    this.add
      .text(centreX, 140, t(`outcome.${result.tier}.summary`, { count: result.rescued }), {
        ...readoutStyle(13),
        wordWrap: { width: panelWidth },
        align: 'center',
      })
      .setOrigin(0.5, 0);

    let y = 196;

    this.add
      .text(centreX, y, t('site.arrived', { hours: Math.round(result.elapsedHours) }), readoutStyle(11, true))
      .setOrigin(0.5);
    y += 20;

    if (result.tier === 'rescue') {
      this.add
        .text(centreX, y, t('site.rescued', { count: result.rescued }), {
          ...readoutStyle(12),
          color: TIER_COLOUR.rescue,
        })
        .setOrigin(0.5);
    } else {
      this.add
        .text(centreX, y, t('site.lost', { count: result.souls }), {
          ...readoutStyle(12),
          color: PALETTE.inkDim,
        })
        .setOrigin(0.5);
    }
    y += 26;

    if (result.beyondWorkingDepth) {
      this.add
        .text(centreX, y, t('site.depth.blocked'), {
          ...readoutStyle(11),
          color: '#e0b070',
          fontStyle: 'italic',
        })
        .setOrigin(0.5);
      y += 24;
    }

    y += 12;
    this.add.text(left, y, t('site.evidence.title'), {
      fontFamily: FONT.mono,
      fontSize: px(11),
      color: PALETTE.inkFaint,
    });
    y += 20;

    if (result.clues.length === 0) {
      this.add
        .text(left, y, t('site.evidence.none'), {
          ...readoutStyle(12),
          color: PALETTE.inkFaint,
          fontStyle: 'italic',
        });
    } else {
      for (const clue of result.clues) {
        const text = this.add.text(left, y, `— ${t(clue.textKey)}`, {
          ...readoutStyle(12),
          wordWrap: { width: panelWidth },
        });
        y += text.height + 12;
      }
    }

    // Zwei Wege zurueck ins Spiel — und das ist die eigentliche Entscheidung
    // dieser Szene. Auf Position bleiben spart die halbe Ueberfahrt zum
    // naechsten Notruf, kostet aber jeden Nachschub: kein Treibstoff, keine
    // Werft, kein Speicherpunkt, bis man wieder daheim ist.
    createButton(this, centreX - 120, height - 56, t('site.stay'), () => {
      audio.stopSonarSweep();
      this.scene.start('WorldMap');
    }, { width: 210 });

    createButton(this, centreX + 120, height - 56, t('site.head_home'), () => {
      audio.stopSonarSweep();
      // Die Heimfahrt ist eine Fahrt wie jede andere und liegt deshalb in der
      // Kampagne — das andere Fahrzeug kann derweil weiterfahren.
      campaign.dispatch({
        vesselId: result.vesselId,
        incident: null,
        speed: 'cruise',
        homebound: true,
      });
      this.scene.start('WorldMap');
    }, { width: 210 });

    this.cameras.main.fadeIn(400, 5, 8, 13);

    audio.startDrone(DRONE_ROOT.site);
    audio.startSea();
    // Am Fundort wird gesucht — dichter getaktet als auf der Ueberfahrt.
    audio.startSonarSweep(5);
    // Direkt ueber dem Wrack: Das Echo kommt fast sofort zurueck.
    audio.onPing = () => audio.sonarEcho(0.2, false);
    audio.setShallowWater(result.incident.depthMetres < 200);
    audio.setTension(0.85);
    audio.playMotif(SITE_MOTIF, 0.08);

    maybeShowTutorial(this, campaign, 'site', { catchUp: true });
    // Ein Fund bekommt ein eigenes Signal — das ist der Ertrag der Mission.
    if (result.clues.length > 0) {
      window.setTimeout(() => audio.cue('discovery'), 900);
    }
  }
}
